"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseBoard = parseBoard;
exports.initBoardLayerSelector = initBoardLayerSelector;
exports.setBoardLayerSelector = setBoardLayerSelector;
exports.readBoardLayer = readBoardLayer;
exports.readModule = readModule;
exports.readText = readText;
exports.parseTrack = parseTrack;
exports.readVia = readVia;
exports.readLine = readLine;
exports.readPad = readPad;
exports.readZone = readZone;
const NodeParser_1 = require("../common/NodeParser");
const layers_1 = require("./layers");
const Area_1 = require("../Area");
const Util_1 = require("../Util");
const v6ParseRectangle_1 = require("./common/v6ParseRectangle");
const v6ParseArc_1 = require("./common/v6ParseArc");
const v6ParseLabel_1 = require("./common/v6ParseLabel");
const v6ExtractPolygon_1 = require("./common/v6ExtractPolygon");
const v6ParseTitleBlock_1 = require("./common/v6ParseTitleBlock");
const v6Common_1 = require("./common/v6Common");
const v6ParseCircle_1 = require("./common/v6ParseCircle");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const v6schema_1 = require("./schematic/v6schema");
const fonts_1 = require("../Fonts/fonts");
const DesignModels_1 = require("@cadstrom/sigma-engine-shared/dist/DesignModels");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const Utils_1 = require("@cadstrom/sigma-engine-shared/dist/Utils");
const nodeIsHidden_1 = require("./common/nodeIsHidden");
const KicadComponentPadCount_1 = require("./KicadComponentPadCount");
let layerSelector;
function parseBoard(project, board, designId, resolver, netRefCtx) {
    if (project.type !== 'kicad') {
        throw new Error('Cannot parse kicad board to non-kicad project');
    }
    initBoardLayerSelector();
    const nets = [];
    const model = {
        modelType: 'pcb',
        designId,
        sourceFile: designId,
        edges_bbox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        initial_zoombox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        grid: v6Common_1.DefaultBrdGrid,
        layerSet: layers_1.KICAD_BRD_LAYERSET,
        throughHoleLayers: [],
        edges: [],
        drawings: {},
        modules: [],
        tracks: {},
        vias: [],
        zones: {},
        nets: {},
        font: {
            default: fonts_1.Font.Newstroke,
        },
        metadata: {
            title: '',
            revision: '',
            company: '',
            date: '',
        },
        warnings: [],
    };
    if (Object.keys(project.components).length === 0) {
        model.warnings.push(`Parse kicad schematics first for proper component linking`);
    }
    const tree = NodeParser_1.SExpressionFileParser.Parse(board);
    function addDrawingToRoot(drawing, layer) {
        const layerNo = typeof layer === 'number' ? layer : layer.id;
        if (!model.drawings[layerNo]) {
            model.drawings[layerNo] = [drawing];
        }
        else {
            model.drawings[layerNo].push(drawing);
        }
    }
    parseBoardSetup(tree.nodes.find((n) => n.name === 'setup')?.nodes, model.warnings);
    if (!project.layerSets[layers_1.KICAD_BRD_LAYERSET]) {
        project.layerSets[layers_1.KICAD_BRD_LAYERSET] = [];
        tree.nodes.forEach((node) => {
            switch (node.name) {
                case 'layers': {
                    node.nodes.forEach((n) => {
                        const layer = readBoardLayer(n);
                        const filtered = layerSelector.filterLayers(layer.name);
                        if (!filtered.length) {
                            model.warnings.push(`Could not parse unexpected layer '${layer.name}' at id '${layer.id}'.`);
                        }
                        else if (filtered.length > 1) {
                            model.warnings.push(`Layer node resolved to multiple layers. This was not expected. '${layer.name}'.`);
                        }
                        else {
                            let defaultVisible = true;
                            if (typeof layer.defaultVisible === 'boolean') {
                                defaultVisible = layer.defaultVisible;
                            }
                            else if (typeof filtered[0].defaultVisible === 'boolean') {
                                defaultVisible = filtered[0].defaultVisible;
                            }
                            project.layerSets[layers_1.KICAD_BRD_LAYERSET].push({
                                ...filtered[0],
                                friendlyName: layer.friendlyName,
                                name: layer.name,
                                defaultVisible,
                            });
                        }
                    });
                    break;
                }
            }
        });
    }
    const activeLayers = new Set(project.layerSets[layers_1.KICAD_BRD_LAYERSET].map((l) => l.id));
    const modules = new Map();
    tree.nodes.forEach((node) => {
        switch (node.name) {
            case 'title_block': {
                (0, v6ParseTitleBlock_1.parseTitleBlock)(model, node);
                break;
            }
            case 'host':
            case 'version': {
                model.metadata[`kicad.${node.name}`] = node.values.map((v) => (0, Util_1.removeQuotes)(v)).join(' ');
                break;
            }
            case 'layers': {
                break;
            }
            case 'footprint':
            case 'module': {
                const moduleDef = readModule(project, model, node, activeLayers);
                if (!modules.has(moduleDef.module.name)) {
                    modules.set(moduleDef.module.name, []);
                }
                modules.get(moduleDef.module.name).push(moduleDef);
                break;
            }
            case 'segment': {
                const track = parseTrack(node);
                const layers = layerSelector.filterLayers(track.layer);
                if (!layers.length) {
                    model.warnings.push(`Could not parse unexpected layer '${track.layer}'.`);
                    break;
                }
                if (layers.length > 1) {
                    model.warnings.push(`Segment has multiple layers. This was not expected. '${track.layer}'`);
                    break;
                }
                const layer = layers[0];
                if (!model.tracks[layer.id]) {
                    model.tracks[layer.id] = [];
                }
                track.track.layer = layer.id;
                track.track.net = nets.find((n) => n.id === track.track.net)?.value ?? '';
                model.tracks[layer.id].push(track.track);
                break;
            }
            case 'via': {
                const via = readVia(node, project.layerSets[layers_1.KICAD_BRD_LAYERSET]);
                via.net = nets.find((n) => n.id === via.net)?.value ?? '';
                model.vias.push(via);
                break;
            }
            case 'gr_line': {
                const line = readLine(node);
                const layers = layerSelector.filterLayers(line.layer);
                if (!layers.length) {
                    model.warnings.push(`Could not parse unexpected layer '${line.layer}'.`);
                    break;
                }
                if (layers.length > 1) {
                    model.warnings.push(`Segment has multiple layers. This was not expected. '${line.layer}'`);
                    break;
                }
                addDrawingToRoot(line.segment, layers[0]);
                break;
            }
            case 'gr_rect':
            case 'rectangle': {
                const drawings = (0, v6ParseRectangle_1.parseRectangle)(node, false, model.warnings, DesignModels_1.PcbLayerType.Other, layerSelector);
                for (const drawing of drawings) {
                    for (const layer of drawing.layers) {
                        addDrawingToRoot(drawing.drawing, layer);
                    }
                }
                break;
            }
            case 'gr_circle': {
                const obj = (0, v6ParseCircle_1.readCircle)(node);
                const layers = layerSelector.filterLayers(obj.layer);
                addDrawingToRoot(obj.circle, layers[0]);
                break;
            }
            case 'arc':
            case 'gr_arc': {
                const drawing = (0, v6ParseArc_1.parseArc)(false, node, false, model.warnings, undefined, layerSelector);
                if (drawing) {
                    for (const layer of drawing.layers) {
                        addDrawingToRoot(drawing.drawing, layer);
                    }
                }
                break;
            }
            case 'gr_text': {
                const result = (0, v6ParseLabel_1.parseTextElement)(node, false, model.warnings, undefined, layerSelector, false);
                addDrawingToRoot(result.drawing, result.layers[0]);
                break;
            }
            case 'gr_poly': {
                const poly = readPolygon(node);
                if (poly.layer) {
                    addDrawingToRoot(poly.polygon, layerSelector.filterLayers(poly.layer)[0]);
                }
                break;
            }
            case 'zone': {
                const zones = readZone(node);
                zones.forEach((zone) => {
                    if (!model.zones[zone.layer]) {
                        model.zones[zone.layer] = [];
                    }
                    model.zones[zone.layer].push(zone);
                });
                break;
            }
            case 'net': {
                nets.push({ id: node.values[0], value: (0, Util_1.removeQuotes)(node.values[1]) });
                break;
            }
        }
    });
    for (let [name, namedModules] of modules) {
        if (namedModules.length > 1 && resolver.resolutionOpts.deduplicateZeroValueComponents) {
            const metaModule = project.modules.find((m) => m.name === name);
            if (metaModule && !isEmptyValue(metaModule.value)) {
                const matchedValue = namedModules.find((m) => m.value === metaModule.value);
                if (matchedValue) {
                    namedModules = [matchedValue];
                }
            }
            const zeroValueModules = namedModules.filter((m) => isEmptyValue(m.value));
            if (zeroValueModules.length > 1) {
                namedModules = namedModules.filter((m) => !isEmptyValue(m.value));
            }
        }
        if (namedModules.length > 1) {
            model.warnings.push(`Module ${name} has multiple definitions that aren't deduplicated.`);
        }
        for (const { footprint, module, value } of namedModules) {
            let existing = resolver.getComponentForFootprint(module, footprint, model);
            if (existing) {
                module.componentRef = existing.name;
                if (!existing.footprints.find((fp) => fp.ref === footprint.ref)) {
                    existing.footprints.push(footprint);
                }
            }
            else {
                module.componentRef = footprint.ref;
                project.components[footprint.ref] = existing = {
                    name: footprint.ref,
                    footprints: [footprint],
                    signals: [],
                    symbols: [],
                    numPins: footprint.pads.length,
                    numPads: footprint.pads.length,
                };
            }
            let metaModule = project.modules.find((m) => m.name === module.name);
            if (!metaModule) {
                metaModule = {
                    name: module.fullModuleName ?? module.name,
                    instances: [],
                    componentRef: module.componentRef,
                };
                if (!metaModule.componentRef ||
                    project.components[metaModule.componentRef]?.metadata?.['ki_keywords'] === 'power-flag') {
                    metaModule.hideInBom = true;
                }
                project.modules.push(metaModule);
            }
            if (!metaModule.name) {
                metaModule.name = module.name;
            }
            if (module.metadata && Object.keys(module.metadata).length > 0) {
                if (!metaModule.metadata) {
                    metaModule.metadata = {};
                }
                metaModule.metadata = {
                    ...metaModule.metadata,
                    ...module.metadata,
                };
            }
            const drawPropertyValue = module.drawProperties['Value'];
            if (drawPropertyValue) {
                drawPropertyValue.value = (0, Util_1.getValueFromMetadata)(drawPropertyValue.value, metaModule);
            }
            if (metaModule.value) {
                metaModule.value = (0, Util_1.getValueFromMetadata)(metaModule.value, metaModule);
            }
            if (value) {
                if (metaModule.value && metaModule.value !== value) {
                    const useValues = Array.from(new Set([metaModule.value, value])).filter((v) => !isEmptyValue(v));
                    if (useValues.length === 1) {
                        metaModule.value = useValues[0];
                    }
                    else {
                        model.warnings.push(`Module ${module.name} has multiple values: ${metaModule.value}, ${value}`);
                        if (module.metadata && Object.prototype.hasOwnProperty.call(module.metadata, 'value')) {
                            module.metadata.value = metaModule.value;
                        }
                        module.drawProperties.value.value = metaModule.value;
                    }
                }
                else {
                    metaModule.value = value;
                }
            }
            metaModule.instances.push({
                ref: module.ref,
                designId,
            });
            model.modules.push(module);
        }
    }
    for (const component of Object.values(project.components)) {
        component.numPads = KicadComponentPadCount_1.KicadComponentPadCount.deriveNumPads(component.footprints);
    }
    const netNames = new Set(nets.map((n) => n.value));
    for (const net of nets) {
        const netInfo = {
            name: net.value,
            isGlobal: false,
            isHierarchical: false,
            connections: model.modules
                .map((m) => {
                const connections = [];
                for (const [pin, pinNet] of Object.entries(m.pinNets)) {
                    if (pinNet === net.value && !connections.find((c) => c.moduleRef === m.ref && c.pinRef === pin)) {
                        connections.push({
                            moduleRef: m.ref,
                            pinRef: pin,
                            moduleName: m.name,
                        });
                    }
                }
                return connections;
            })
                .flat(),
        };
        model.nets[net.value] = netInfo;
        (0, v6schema_1.kicadAddNetDifferentialPairInfo)(net.value, netInfo, netNames, netRefCtx, model.warnings);
    }
    const bbox = new Area_1.default();
    bbox.extendFromPcbModel(project, model, project.layerSets[layers_1.KICAD_BRD_LAYERSET], model.warnings);
    model.edges_bbox = {
        minx: bbox.xMin,
        miny: bbox.yMin,
        maxx: bbox.xMax,
        maxy: bbox.yMax,
    };
    const visibleBbox = new Area_1.default();
    visibleBbox.extendFromPcbModel(project, model, project.layerSets[layers_1.KICAD_BRD_LAYERSET], model.warnings, false);
    model.initial_zoombox = {
        minx: visibleBbox.xMin,
        miny: visibleBbox.yMin,
        maxx: visibleBbox.xMax,
        maxy: visibleBbox.yMax,
    };
    (0, Util_1.dedupeWarnings)(model);
    layerSelector = undefined;
    return model;
}
function initBoardLayerSelector(s) {
    return (layerSelector = s || new layers_1.KicadPcbLayerSelector());
}
function setBoardLayerSelector(s) {
    layerSelector = s;
}
function readBoardLayer(node) {
    const index = parseInt(node.name);
    let [name, attribute] = node.values;
    name = (0, Util_1.removeQuotes)(name);
    let defaultVisible;
    let friendlyName;
    for (const value of node.values) {
        if (value.startsWith('"')) {
            const unquoted = (0, Util_1.removeQuotes)(value);
            if (unquoted !== name) {
                friendlyName = unquoted;
            }
        }
        else if (value === 'hide' || value === 'hidden') {
            defaultVisible = false;
        }
    }
    return { id: index, name, friendlyName, attribute, defaultVisible };
}
function readModule(project, model, node, activeLayers) {
    let value;
    const footprint = {
        ref: (0, Util_1.removeQuotes)(node.values[0]),
        name: (0, Util_1.removeQuotes)(node.values[0]),
        pads: [],
        drawings: [],
    };
    const module = {
        ref: '',
        footprintRef: footprint.ref,
        componentRef: footprint.ref,
        center: [0, 0],
        transform: {
            pos: [0, 0],
            angle: 0,
            mirrored: [],
        },
        layer: 'F',
        name: '',
        pinNets: {},
        drawProperties: {},
    };
    function addMetadata(key, value, obj = footprint) {
        value = (0, Util_1.cleanMetadataVal)(value);
        if (!value || value === '~') {
            return;
        }
        if (!obj.metadata) {
            obj.metadata = {};
        }
        obj.metadata[key] = value;
    }
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                const x = parseFloat(n.values[0]);
                const y = parseFloat(n.values[1]);
                const rotation = n.values.length > 2 ? parseFloat(n.values[2]) : 0;
                module.center = [(0, Transform_1.roundToMaxOutputPositionPrecision)(x), (0, Transform_1.roundToMaxOutputPositionPrecision)(y)];
                module.transform.angle = (0, Transform_1.roundToMaxOutputAnglePrecision)(rotation);
                break;
            }
            case 'property':
            case 'fp_text': {
                const obj = readText(n);
                let drawPropertyName = obj.text.ref;
                let drawProperty;
                if (obj.text.ref && obj.text.ref_designator) {
                    if (obj.text.ref.toLowerCase() === 'user') {
                        drawPropertyName = obj.text.ref = `u:${obj.text.value}`;
                    }
                    drawProperty = {
                        value: obj.text.value,
                    };
                    if (obj.hidden) {
                        drawProperty.hidden = obj.hidden;
                    }
                }
                if (drawProperty && drawPropertyName) {
                    module.drawProperties[drawPropertyName] = {
                        pos: obj.text.pos,
                        angle: obj.text.angle,
                        isPositionOffset: true,
                        rejustifyOnRotate: obj.text.rejustifyOnRotate,
                        ...drawProperty,
                    };
                    addMetadata(drawPropertyName, drawProperty.value, module);
                }
                if (obj.text.ref?.toLowerCase() === 'reference') {
                    module.name = obj.text.value;
                }
                if (obj.text.val) {
                    value = obj.text.value.trim();
                }
                if (obj.text.ref) {
                    obj.text.value = '';
                }
                const layers = layerSelector.filterLayers(obj.layer);
                footprint.drawings.push({
                    layers: layers.map((l) => l.id),
                    drawing: obj.text,
                });
                break;
            }
            case 'fp_circle': {
                const obj = (0, v6ParseCircle_1.readCircle)(n);
                const layers = layerSelector.filterLayers(obj.layer);
                footprint.drawings.push({
                    layers: layers.map((l) => l.id),
                    drawing: obj.circle,
                });
                break;
            }
            case 'fp_line': {
                const obj = readLine(n);
                const layers = layerSelector.filterLayers(obj.layer);
                footprint.drawings.push({
                    layers: layers.map((l) => l.id),
                    drawing: obj.segment,
                });
                break;
            }
            case 'fp_poly': {
                const poly = readPolygon(n, true);
                if (poly.layer) {
                    footprint.drawings.push({
                        layers: layerSelector.filterLayers(poly.layer).map((l) => l.id),
                        drawing: poly.polygon,
                    });
                }
                break;
            }
            case 'arc':
            case 'gr_arc': {
                const drawing = (0, v6ParseArc_1.parseArc)(false, node, true, model.warnings, undefined, layerSelector);
                if (drawing) {
                    footprint.drawings.push(drawing);
                }
                break;
            }
            case 'pad': {
                const { pad, net } = readPad(model, n, module.transform.angle, activeLayers);
                footprint.pads.push(pad);
                if (pad.ref && net && net.length > 0) {
                    module.pinNets[pad.ref] = connectToSchematicNet(project, model, module, pad, net);
                }
                break;
            }
            case 'uuid': {
                if (n.values.length > 0) {
                    module.ref = (0, Util_1.removeQuotes)(n.values[0]);
                }
                break;
            }
            case 'path': {
                if (n.values.length > 0) {
                    module.ref = (0, Util_1.removeQuotes)(n.values[0]);
                }
                break;
            }
            case 'descr':
            case 'tags':
            case 'attr':
                addMetadata(n.name, (0, Util_1.removeQuotes)(n.values[0]));
                break;
            case 'layer': {
                const layer = layerSelector?.filterLayers((0, Util_1.removeQuotes)(n.values[0]))?.find((l) => l.face);
                if (layer?.face) {
                    module.layer = layer.face;
                }
                break;
            }
        }
    });
    for (const [key, value] of Object.entries(module.drawProperties)) {
        if (key.startsWith('u:${')) {
            const refMatch = key.substring(4, key.length - 1).toLowerCase();
            if (module.drawProperties[refMatch]) {
                value.value = module.drawProperties[refMatch].value;
            }
            else {
                model.warnings.push(`Could not resolve value for ${key} on fp ${module.ref}: ${refMatch}`);
            }
        }
        else if (key.startsWith('u:%')) {
            switch (key.toLowerCase()) {
                case 'u:%r':
                    value.value = module.drawProperties['reference'].value;
                    break;
                case 'u:%v':
                    value.value = module.drawProperties['value'].value;
                    break;
                default:
                    model.warnings.push(`Unknown user variable: ${key} on fp ${module.ref}`);
            }
        }
        value.angle = (0, Transform_1.roundToMaxOutputAnglePrecision)((0, Transform_1.clampRotation)(-module.transform.angle + (value.angle || 0)));
    }
    return { footprint, module, value };
}
function readText(node) {
    let x = -1;
    let y = -1;
    let fontSize = -1;
    let thickness = -1;
    let layerName = '';
    let rotation = 0;
    let attr;
    let unlocked = false;
    const refType = (0, Util_1.removeQuotes)(node.values[0].toLowerCase());
    const value = (0, Util_1.removeQuotes)(node.values[1]);
    const hidden = (0, nodeIsHidden_1.nodeIsHidden)(node);
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                x = parseFloat(n.values[0]);
                y = parseFloat(n.values[1]);
                unlocked = n.values.indexOf('unlocked') !== -1;
                rotation = n.values.length > 2 && n.values[2] !== 'unlocked' ? parseFloat(n.values[2]) : 0;
                break;
            }
            case 'effects': {
                const fontNode = n.nodes.find((n) => n.name === 'font');
                if (fontNode) {
                    const sizeNode = fontNode.nodes.filter((n) => n.name === 'size');
                    if (sizeNode?.length) {
                        fontSize = parseFloat(sizeNode[0].values[0]);
                    }
                    const thicknessNode = fontNode.nodes.filter((n) => n.name === 'thickness');
                    if (thicknessNode?.length) {
                        thickness = parseFloat(thicknessNode[0].values[0]);
                    }
                    if (fontNode.values.indexOf('bold') !== -1) {
                        if (!attr) {
                            attr = [];
                        }
                        attr.push('bold');
                    }
                }
                break;
            }
            case 'layer': {
                layerName = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
        }
    });
    const text = {
        type: 'text',
        pos: [x, y],
        angle: rotation,
        attr,
        justify: [0, 0],
        value: value,
        size: fontSize,
        thickness,
    };
    if (!unlocked) {
        text.rejustifyOnRotate = true;
    }
    if (refType) {
        text.ref = refType;
        text.ref_designator = true;
        if (refType === 'value') {
            text.val = true;
        }
    }
    return { text, layer: layerName, hidden };
}
function parseTrack(node) {
    let xi = -1;
    let xf = -1;
    let yi = -1;
    let yf = -1;
    let layer = '';
    let width = 0;
    let net = '';
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'start': {
                xi = parseFloat(n.values[0]);
                yi = parseFloat(n.values[1]);
                break;
            }
            case 'end': {
                xf = parseFloat(n.values[0]);
                yf = parseFloat(n.values[1]);
                break;
            }
            case 'layer': {
                layer = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
            case 'width': {
                width = parseFloat(n.values[0]);
                break;
            }
            case 'net': {
                net = n.values[0];
                break;
            }
        }
    });
    const track = {
        type: 'track',
        start: [xi, yi],
        end: [xf, yf],
        width,
        net,
        layer: -1,
    };
    return { layer, track };
}
function readVia(node, modelLayers) {
    let center = [0, 0];
    let size = 0;
    let drillSize = 0;
    let layers = [];
    let net = '';
    const [type] = node.values;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                center = [parseFloat(n.values[0]), parseFloat(n.values[1])];
                break;
            }
            case 'size': {
                size = parseFloat(n.values[0]);
                break;
            }
            case 'drill': {
                drillSize = parseFloat(n.values[0]);
                break;
            }
            case 'layers': {
                const [start, end] = n.values
                    .map((l) => (0, Util_1.removeQuotes)(l))
                    .map((l) => layerSelector.filterLayers(l))
                    .flat()
                    .map((l) => l.id)
                    .sort();
                layers = modelLayers.filter((l) => l.id >= start && l.id <= end).map((l) => l.id);
                break;
            }
            case 'net': {
                net = n.values[0];
                break;
            }
        }
    });
    return {
        type: 'via',
        center,
        diameter: size,
        drillDiameter: drillSize,
        layers,
        net,
        shape: 'circle',
        metadata: {
            type,
        },
    };
}
function readLine(node) {
    let xi = -1;
    let xf = -1;
    let yi = -1;
    let yf = -1;
    let layer = '';
    let width = 0;
    let style;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'start': {
                xi = parseFloat(n.values[0]);
                yi = parseFloat(n.values[1]);
                break;
            }
            case 'end': {
                xf = parseFloat(n.values[0]);
                yf = parseFloat(n.values[1]);
                break;
            }
            case 'layer': {
                layer = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
            case 'width': {
                width = parseFloat(n.values[0]);
                break;
            }
            case 'stroke': {
                const widthNode = n.nodes.find((n2) => n2.name === 'width');
                if (widthNode) {
                    width = parseFloat(widthNode.values[0]);
                }
                const typeNode = n.nodes.find((n2) => n2.name === 'type');
                if (typeNode) {
                    style = (0, v6Common_1.parseKicadLineType)(typeNode.values[0]);
                }
                break;
            }
        }
    });
    const segment = {
        type: 'segment',
        start: [xi, yi],
        end: [xf, yf],
        width,
        style,
    };
    return { layer, segment };
}
function readPolygon(node, isModule = false) {
    const { points, strokeWidth, style, fillType, layerName } = (0, v6ExtractPolygon_1.extractPolygon)(isModule, node, true);
    const polygon = {
        type: 'polygon',
        filled: fillType !== 'none',
        width: (0, Utils_1.max)(strokeWidth, 0),
        points,
        lineStyle: style,
    };
    return {
        polygon,
        layer: layerName,
    };
}
function readPad(model, node, parentRotation, activeLayers) {
    let x = 0, y = 0;
    let rotation = 0;
    let sizeX = 0, sizeY = 0;
    let layers = [];
    let net = '';
    let hasDrill = false;
    let isOval = false;
    let drillX = 0;
    let drillY = 0;
    let [value, padType, shape] = node.values;
    value = (0, Util_1.removeQuotes)(value);
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                x = parseFloat(n.values[0]);
                y = parseFloat(n.values[1]);
                rotation = n.values.length > 2 ? parseFloat(n.values[2]) : 0;
                break;
            }
            case 'size': {
                sizeX = parseFloat(n.values[0]);
                sizeY = parseFloat(n.values[1]);
                break;
            }
            case 'layers': {
                layers = n.values
                    .map((l) => (0, Util_1.removeQuotes)(l))
                    .map((l) => layerSelector.filterLayers(l))
                    .flat();
                break;
            }
            case 'drill': {
                hasDrill = true;
                isOval = n.values[0] === 'oval';
                drillX = isOval ? parseFloat(n.values[1]) : parseFloat(n.values[0]);
                drillY = n.values.length > 2 ? parseFloat(n.values[2]) : drillX;
                break;
            }
            case 'net': {
                net = (0, Util_1.removeQuotes)(n.values[1]);
                break;
            }
        }
    });
    const isPad1 = ['1', 'A', 'A1', 'P1', 'PAD1'].includes(value);
    let angle = rotation - parentRotation;
    while (angle >= 360) {
        angle -= 360;
    }
    while (angle < 0) {
        angle += 360;
    }
    const layersFormatted = layers.map((l) => l.id);
    const pad = {
        type: 'pad',
        ref: value,
        name: value,
        layers: layersFormatted.filter((s, index) => layersFormatted.indexOf(s) === index && activeLayers.has(s)),
        pos: [x, y],
        angle,
        pin1: isPad1 ? true : undefined,
        shape: shape,
        padType: ['thru_hole', 'np_thru_hole'].includes(padType) ? 'th' : 'smd',
        diameter: [sizeX, sizeY],
    };
    switch (pad.shape) {
        case 'circle':
        case 'rect':
            break;
        case 'oval':
            pad.shape = 'roundrect';
            pad.radius = Math.round((0, Utils_1.min)(sizeX, sizeY) * Math.PI * 100);
            break;
        case 'roundrect': {
            const radius = node.nodes.find((n) => n.name === 'roundrect_rratio')?.values[0];
            if (radius) {
                pad.radius = parseFloat(radius) * (0, Utils_1.min)(sizeX, sizeY);
            }
            break;
        }
        case 'custom':
        default:
            model.warnings.push(`Kicad pad shape '${shape}' not supported yet. Rendering as rect.`);
            pad.shape = 'rect';
            break;
    }
    if (hasDrill) {
        pad.drillshape = isOval ? 'oblong' : 'circle';
        pad.drillDiameter = [drillX, drillY];
    }
    return { pad, net };
}
function readZone(node) {
    let layers = [];
    let isFilled = false;
    const polygons = [];
    let netName = '';
    let minThickness = 0;
    let filledAreaThickness = false;
    const results = [];
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'net_name': {
                netName = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
            case 'min_thickness': {
                minThickness = parseFloat(n.values[0]);
                break;
            }
            case 'filled_areas_thickness': {
                filledAreaThickness = n.values[0] === 'yes';
                break;
            }
            case 'layer':
            case 'layers': {
                layers = n.values
                    .map((l) => (0, Util_1.removeQuotes)(l))
                    .map((l) => layerSelector.filterLayers(l))
                    .flat()
                    .map((l) => l.id);
                break;
            }
            case 'fill': {
                isFilled = n.values[0] === 'yes';
                break;
            }
            case 'polygon': {
                const polygon = [];
                const pts = n.nodes.find((child) => child.name === 'pts');
                pts?.nodes.forEach((pt) => polygon.push([parseFloat(pt.values[0]), parseFloat(pt.values[1])]));
                polygons.push({ points: polygon, filled: false });
                break;
            }
            case 'filled_polygon': {
                const layer = n.nodes.find((child) => child.name === 'layer');
                const pts = n.nodes.find((child) => child.name === 'pts');
                if (!pts) {
                    break;
                }
                const polyLayers = layer ? layerSelector.filterLayers((0, Util_1.removeQuotes)(layer.values[0])).map((l) => l.id) : layers;
                polyLayers.forEach((l) => {
                    results.push({
                        layer: l,
                        polygon: closePolygon({
                            points: pts.nodes.map((pt) => [parseFloat(pt.values[0]), parseFloat(pt.values[1])]),
                            filled: true,
                            lineWidth: 0,
                        }),
                    });
                });
                break;
            }
        }
    });
    return results.map((r) => ({
        type: 'zone',
        polygons: [r.polygon],
        net: netName,
        layer: r.layer,
        metadata: {
            isFilled,
            minThickness,
            filledAreaThickness,
        },
    }));
}
function parseBoardSetup(nodes, warnings) {
    const setup = {
        stackup: {
            layers: [],
        },
        clearances: {},
        pcbPlotParams: {},
        properties: {},
    };
    if (!nodes) {
        warnings.push(`Kicad PCB did not contain setup information`);
        return setup;
    }
    nodes.forEach((n) => {
        switch (n.name) {
            case 'pad_to_mask_clearance':
                setup.clearances[n.name] = parseFloat(n.values[0]);
                break;
            case 'pcbplotparams': {
                n.nodes.forEach((n2) => {
                    const value = n2.values[0];
                    if (value === 'true' || value === 'false') {
                        setup.pcbPlotParams[n2.name] = value === 'true';
                    }
                    else if (value.startsWith('"')) {
                        setup.pcbPlotParams[n2.name] = (0, Util_1.removeQuotes)(value);
                    }
                    else if (value.length > 0 &&
                        /^-?[0-9]*\.?[0-9]*$/.test(value) &&
                        value.indexOf('.') === value.lastIndexOf('.')) {
                        setup.pcbPlotParams[n2.name] = parseFloat(value);
                    }
                    else if (value.length > 0 && /^0x[0-9a-f]+(_[0-9a-f]+)*$/.test(value)) {
                        setup.pcbPlotParams[n2.name] = parseInt(value.substring(2).replace('_', ''), 16);
                    }
                    else {
                        warnings.push(`Could not parse pcb plt param ${n2.name}`);
                    }
                });
                break;
            }
            case 'stackup': {
                n.nodes.forEach((n2) => {
                    switch (n2.name) {
                        case 'layer': {
                            setup.stackup.layers.push(parseBoardStackupLayer(n2, warnings));
                            break;
                        }
                        case 'copper_finish': {
                            setup.stackup.copperFinish = n2.values[0];
                            break;
                        }
                        case 'dielectric_constraints': {
                            setup.stackup.dialectricConstraints = n2.values[0] !== 'no';
                            break;
                        }
                        default: {
                            warnings.push(`Unknown stack param: '${n2.name}'`);
                            break;
                        }
                    }
                });
                break;
            }
            default: {
                if (n.values.length === 1) {
                    let value = n.values[0];
                    if (n.values[0] === 'yes' || n.values[0] === 'no') {
                        value = value === 'yes';
                    }
                    else if (Number.isFinite(parseFloat(value))) {
                        value = parseFloat(value);
                    }
                    setup.properties[n.name] = value;
                }
                else if (n.values.length > 1) {
                    setup.properties[n.name] = n.values.map((value) => (0, Util_1.removeQuotes)(value)).join(' ');
                }
                break;
            }
        }
    });
    return setup;
}
function parseBoardStackupLayer(node, warnings) {
    const layer = {
        layerName: node.values[0],
        otherProps: {},
        type: '',
    };
    node.nodes.forEach((n) => {
        let value = n.values[0];
        if (value === 'true' || value === 'false') {
            value = value === 'true';
        }
        else if (value.startsWith('"')) {
            value = (0, Util_1.removeQuotes)(value);
        }
        else if (value.length > 0 && /^-?[0-9]*\.?[0-9]*$/.test(value) && value.indexOf('.') === value.lastIndexOf('.')) {
            value = parseFloat(value);
        }
        else {
            warnings.push(`Could not parse pcb plt param ${n.name}`);
        }
        switch (n.name) {
            case 'thickness':
            case 'material':
            case 'epsilon_r':
            case 'loss_tangent':
            case 'type': {
                layer[n.name] = value;
                break;
            }
            default:
                warnings.push(`Unknown pcb layer prop ${n.name}`);
        }
    });
    return layer;
}
function closePolygon(poly) {
    const lastPoint = poly.points[poly.points.length - 1];
    if (poly.points[0][0] !== lastPoint[0] || poly.points[0][1] !== lastPoint[1]) {
        poly.points.push(poly.points[0]);
    }
    return poly;
}
function correctNetNames(project, model, warnings, netRefCtx) {
    for (const module of model.modules) {
        const component = project.components[module.componentRef];
        if (!component) {
            warnings.push(`Could not find component ${module.componentRef} for module ${module.ref}`);
            continue;
        }
        const projectModule = project.modules.find((m) => m.name === module.name);
        if (!projectModule) {
            warnings.push(`Could not find project module ${module.name} for module ${module.ref}`);
            continue;
        }
        for (const [pad, net] of Object.entries(module.pinNets)) {
            const signal = component.signals.find((s) => s.padRefs.includes(pad));
            if (!signal) {
                warnings.push(`Could not find signal for pin ${pad} on component ${component.name} for module ${module.ref}`);
                continue;
            }
            for (const { designId, ref: instanceRef } of projectModule.instances) {
                if (designId === model.designId) {
                    continue;
                }
                const design = project.designs[designId];
                if (!design) {
                    warnings.push(`Could not find design ${designId} for module ${module.ref}`);
                    continue;
                }
                const moduleMatch = design.modules.find((m) => m.ref === instanceRef);
                if (!moduleMatch) {
                    continue;
                }
                for (const pinRef of signal[design.modelType === 'schematic' ? 'pinRefs' : 'padRefs']) {
                    if (!pinRef) {
                        continue;
                    }
                    const ref = moduleMatch.pinNets[pinRef];
                    if (ref instanceof sigma_engine_shared_1.NetRef) {
                        const oldValue = ref.valueOf();
                        if (oldValue !== net && design.nets[oldValue]?.connections.length) {
                            if (design.nets[net]) {
                                warnings.push(`NetCombobulatorMiss: Net ${net} already exists in design ${designId}. Merging...`);
                                if (net.valueOf() !== oldValue.valueOf()) {
                                    design.nets[net].connections.push(...design.nets[oldValue].connections);
                                }
                            }
                            else {
                                design.nets[net] = design.nets[oldValue];
                            }
                            ref.setName(net);
                        }
                    }
                    else if (!ref) {
                        warnings.push(`Pin ${pinRef} is missing net assignment to ${net}`);
                    }
                    else {
                        warnings.push(`NetRef ${pinRef} is not a NetRef`);
                    }
                    if (design.nets[net] && !design.nets[net]?.diffPair) {
                        (0, v6schema_1.kicadAddNetDifferentialPairInfo)(net, design.nets[net], new Set(Object.keys(design.nets)), netRefCtx, warnings);
                    }
                }
            }
        }
    }
}
function isEmptyValue(value) {
    return value === undefined || value === null;
}
function connectToSchematicNet(project, model, module, pad, net) {
    if (!project) {
        return net;
    }
    const netWithSlash = net.startsWith('/') ? net : `/${net}`;
    const component = project.components[module.componentRef];
    if (!component) {
        model.warnings.push(`Could not find component ${module.componentRef} for module ${module.ref}`);
        return net;
    }
    const signal = component.signals.find((s) => s.pinRefs.includes(pad.ref));
    if (!signal) {
        model.warnings.push(`Could not find signal for pin ${pad.ref} on component ${component.name} for module ${module.ref}`);
        return net;
    }
    const metaModule = project.modules.find((m) => m.name === module.name);
    if (!metaModule) {
        model.warnings.push(`Could not find meta module ${module.name} for module ${module.ref}`);
        return net;
    }
    for (const instance of metaModule.instances) {
        const module = project.designs[instance.designId].modules.find((m) => m.ref === instance.ref);
        if (!module) {
            model.warnings.push(`Could not find module ${instance.ref} in design ${instance.designId}`);
            continue;
        }
        for (const padRef of signal.padRefs) {
            if (!padRef) {
                continue;
            }
            const ref = module.pinNets[padRef];
            if (!ref) {
                continue;
            }
            if (ref.endsWith(netWithSlash)) {
                return ref;
            }
            if ((0, sigma_engine_shared_1.isNetRef)(ref)) {
                ref.setName(netWithSlash);
                return ref;
            }
        }
    }
    return net;
}
function assertBoardNetNames(project, model) {
    for (const [netName, netInfo] of Object.entries(model.nets)) {
        for (const connection of netInfo.connections) {
            const srcModule = model.modules.find((m) => m.ref === connection.moduleRef);
            for (const [, design] of Object.entries(project.designs)) {
                const module = design.modules.find((m) => m.name === srcModule?.name);
                if (!module) {
                    continue;
                }
                const component = project.components[module.componentRef];
                if (!component) {
                    continue;
                }
                const signal = component.signals.find((s) => s.padRefs.includes(connection.pinRef));
                if (!signal) {
                    continue;
                }
                for (const checkPin of signal[design.modelType === 'schematic' ? 'pinRefs' : 'padRefs']) {
                    if (checkPin && module.pinNets[checkPin]?.valueOf() !== netName.valueOf()) {
                        if ((0, sigma_engine_shared_1.isNetRef)(module.pinNets[checkPin])) {
                            module.pinNets[checkPin].setName(netName);
                        }
                        else {
                            module.pinNets[checkPin] = netName;
                        }
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=board.js.map